/**
 * The page a sign-in was started from, carried through Keycloak and back so the callback can
 * return there instead of to the landing page.
 *
 * A session that ran out while the buyer was on Stripe is the case this exists for: "Back to
 * SignalCard" on the payment-success page opens the product's route, whose protected route
 * starts a fresh sign-in, and the draft kept for the trip waits on that product's page.
 *
 * The path travels in the OIDC state, which oidc-client-ts keeps in this tab's storage rather
 * than sending anywhere. It is still read back as untrusted: only a path on this site is followed.
 */

export interface SignInReturnState {
  returnTo: string;
}

const hasControlCharacter = (text: string): boolean => {
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
};

/** Where the browser is now, as a path on this site: the page to come back to. */
export const currentReturnState = (): SignInReturnState => ({
  returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
});

/**
 * The path to go to once signed in: `state.returnTo` when it is a path on this site, and "/"
 * for anything else. A path here is one leading slash and nothing a browser could read as
 * another origin: no second slash straight after it (`//evil.example` is a protocol-relative
 * URL), no backslash (browsers read `/\evil.example` the same way), no scheme, and no control
 * characters, which URL parsing drops. What is left must still resolve to this origin.
 */
export const signInReturnPath = (state: unknown): string => {
  const returnTo = (state as Partial<SignInReturnState> | null | undefined)
    ?.returnTo;
  if (typeof returnTo !== "string" || !returnTo.startsWith("/")) return "/";
  if (returnTo.startsWith("//") || returnTo.includes("\\")) return "/";
  if (hasControlCharacter(returnTo)) return "/";
  try {
    const origin = window.location.origin;
    return new URL(returnTo, origin).origin === origin ? returnTo : "/";
  } catch {
    return "/";
  }
};
